// @ts-check
import React, { useCallback, useContext, useMemo } from 'react';
import _ from 'lodash';
import { EmptyState, EmptyStateIcon, EmptyStateBody, Spinner, EmptyStateHeader } from '@patternfly/react-core';

import { useTranslation } from 'localization/useTranslation';
import { Icons } from '../Common';
import Dashboard from '../Dashboard/Dashboard';
import Tabs from '../Tabs/Tabs';
import dashboardTabs from '../Asset/AssetView/dashboardTabs';
import styles from './NodeDashboardPage.module.css';
import { Size } from '../Common/Icons';
import { throttleDate } from 'services/datetime';
import useDashboardsQuery from 'components/Dashboard/useDashboardsQuery';
import { AuthService } from 'services/AuthService/AuthService';
import AlertContext from 'services/AlertContext';

/**
 * Gets a list of home dashboards
 * @return {[homeDashboards: import('components/Dashboard/DashboardPropTypes').DashboardType[], isLoading: boolean]}
 */
const useHomeDashboards = () => {
  const { user } = useContext(AuthService);
  const homeDashboardIds = useMemo(() => {
    return _.reduce(user.effectiveOrgs, (result, org) => {
      return _.union(result, org?.settings?.homeDashboards ?? []);
    }, []);
  }, [user.effectiveOrgs]);

  const { addErrorAlert } = useContext(AlertContext);
  const T = useTranslation();
  const dashboardsQuery = useDashboardsQuery({
    onError: useCallback(() => {
      addErrorAlert(T.dashboards.failedtoload);
    }, [T.dashboards.failedtoload, addErrorAlert])
  });

  const homeDashboards = useMemo(() => {
    return _.filter(dashboardsQuery.data, dashboard => _.includes(homeDashboardIds, dashboard.id));
  }, [dashboardsQuery.data, homeDashboardIds]);
  return [homeDashboards, dashboardsQuery.isLoading];
};

/**
 * Render home dashboards
 * @returns {React.ReactElement}
 */
const NodeDashboardPage = () => {
  const T = useTranslation();
  const [homeDashboards, isLoading] = useHomeDashboards();
  // Throttle dashboard date so network request can cache on same time.
  const dashboardDate = useMemo(() => throttleDate(1000), []);

  return (
    <>
      { !_.isEmpty(homeDashboards) &&
        ((_.size(homeDashboards) === 1 &&
          <div className={styles.homeDashboard}><Dashboard dashboard={homeDashboards[0]} dashboardDate={dashboardDate}/></div>) ||
          <div className={styles.homeDashboardTabsWrapper}>
            <Tabs
              tabClassName={styles.homeDashboardTabsContent}
              tabs={dashboardTabs(homeDashboards, dashboardDate)} />
          </div>)
      }
      { isLoading &&
        <div className={styles.homeDashboardContent}>
          <Spinner size="md" />
        </div>
      }
      { !isLoading && (
        _.isEmpty(homeDashboards) && <EmptyState>
          <EmptyStateHeader titleText={<>{ T.common.noDashboardsTitle }</>} icon={<EmptyStateIcon icon={() => <Icons.Dashboard size={Size.XL} />} />} headingLevel="h4" />
          <EmptyStateBody>
            { T.common.noDashboardsDescription }
          </EmptyStateBody>
        </EmptyState>)
      }
    </>
  );
};

export default React.memo(NodeDashboardPage);
